import { FORMAT_DATE_STRING } from '@/components/deal/payments/add-payment';
import { Payment } from '@/models/registry';
import type { Payment as PaymentType } from '@/models/types';
import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';

export class PaymentStore implements BaseStore<Payment, PaymentType> {
  store: Map<string, Payment> = new Map();

  storeIndexedByForeignKey: Map<string, Payment[]> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  private setPaymentField = <K extends keyof PaymentType>(
    payment: Payment,
    key: K,
    value: PaymentType[K],
  ) => {
    (payment as Pick<Payment, K>)[key] = value as Payment[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<PaymentType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.deal_payments.delete(delta.record_id);
      }
      this.remove(delta.record_id, delta.data.deal_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.deal_payments.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.deal_payments.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.deal_payments.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<PaymentType>;
  }) {
    const existingPayment = this.getById(delta.record_id);
    if (!existingPayment) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof PaymentType,
      PaymentType[keyof PaymentType],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingPayment[key] === newValue) {
        continue;
      }

      this.setPaymentField(existingPayment, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingPayment);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<PaymentType>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<PaymentType>;
    hydrateIndexedDB?: boolean;
  }) {
    if (hydrateIndexedDB) {
      db.deal_payments.put(delta.data, delta.record_id);
    }

    if (delta.action === 'delete') {
      this.softDelete(delta.record_id);
      return;
    }

    if (delta.action === 'archive') {
      this.softDelete(delta.record_id);
      return;
    }

    this.upsert(delta.data);
  }

  getById(id: string): Payment | undefined {
    return this.store.get(id);
  }

  add(input: PaymentType) {
    const payment = new Payment(input);
    this.store.set(payment.id, payment);
    // Index by foreign key for O(1) lookups
    const arr = this.storeIndexedByForeignKey.get(payment.deal_id) || [];
    this.storeIndexedByForeignKey.set(payment.deal_id, [...arr, payment]);
  }

  save(payment: Payment) {
    this.store.set(payment.id, payment);
    // update the payment in the storeIndexedByForeignKey
    const arr = this.storeIndexedByForeignKey.get(payment.deal_id) || [];
    this.storeIndexedByForeignKey.set(
      payment.deal_id,
      arr.map((p) => (p.id === payment.id ? payment : p)),
    );
  }

  replace({ id, payment }: { id: Payment['id']; payment: PaymentType }) {
    const existingPayment = this.getById(id);

    if (!existingPayment) return;

    existingPayment.amount = payment.amount;
    existingPayment.intent_date = payment.intent_date;
    existingPayment.sent_date = payment.sent_date;
    existingPayment.received_date = payment.received_date;
    existingPayment.confirmed_date = payment.confirmed_date;
    existingPayment.refunded_date = payment.refunded_date;
    existingPayment.receipt_uploaded_by_id = payment.receipt_uploaded_by_id;
    existingPayment.receipt_file_url = payment.receipt_file_url;
    existingPayment.receipt_file_name = payment.receipt_file_name;
    existingPayment.receipt_file_type = payment.receipt_file_type;
    existingPayment.receipt_file_size = payment.receipt_file_size;
    existingPayment.method = payment.method;
    existingPayment.type = payment.type;
    existingPayment.is_full_payment = payment.is_full_payment;
    existingPayment.created_by_id = payment.created_by_id;
    existingPayment.created_at = payment.created_at;
    existingPayment.updated_at = payment.updated_at;
    existingPayment.deleted_at = payment.deleted_at;
    this.save(existingPayment);
  }

  upsert(payment: PaymentType) {
    const existingPayment = this.getById(payment.id);
    if (existingPayment) {
      this.replace({ id: payment.id, payment });
    } else {
      this.add(payment);
    }
  }

  remove(id: Payment['id'], dealId: Payment['deal_id']) {
    this.store.delete(id);

    // remove from the index
    const arr = this.storeIndexedByForeignKey.get(dealId) || [];
    this.storeIndexedByForeignKey.set(
      dealId,
      arr.filter((p) => p.id !== id),
    );

    // remove from dexie
    db.deal_payments.delete(id);
  }

  softDelete(id: Payment['id']) {
    const payment = this.getById(id);
    if (!payment) return;
    payment.deleted_at = dayjs().toISOString();

    this.save(payment);
  }

  restore(id: Payment['id']) {
    const payment = this.getById(id);
    if (!payment) return;
    payment.deleted_at = null;

    this.save(payment);
  }

  uploadReceipt(id: Payment['id']) {
    const payment = this.getById(id);
    if (!payment) return;
    payment.confirmed_date = dayjs().format(FORMAT_DATE_STRING);

    this.save(payment);
  }

  hydrateLocal(): Promise<void> {
    return hydrateFromWorker({
      tableName: 'deal_payments',
      entity: 'deal_payments',
      modelStore: paymentStore,
    });
  }

  async hydrateDeal(dealId: string): Promise<void> {
    const payments = await db.deal_payments
      .where('deal_id')
      .equals(dealId)
      .toArray();
    payments.forEach((payment) => this.upsert(payment));
  }
}

export const paymentStore = new PaymentStore();
